<template>
  <MainLayout :title="trans('Track your order')">
    <div
      class="sticky top-0 z-10 bg-theme-background"
      style="padding-top: var(--safe-area-inset-top)"
    >
      <div class="flex items-center p-4">
        <ADeepCircleButton
          class="flex-shrink-0"
          @click="router.push({ name: 'root' })"
        >
          <template #icon><ArrowLeftBold /></template>
        </ADeepCircleButton>
        <div
          class="flex-1 truncate px-4 text-center font-primary text-lg font-medium"
        >
          {{ trans('Track your order') }}
        </div>
        <div class="h-10 w-10 flex-shrink-0"></div>
      </div>
    </div>
    <div v-if="order" class="mx-auto max-w-xl p-4">
      <div class="mt-4 text-center text-3xl font-bold">
        {{ trans('Order number') }} #{{ order.orderNumber }}
      </div>
      <div v-if="meta && meta.phoneNumber" class="mt-2 text-center">
        {{
          trans('If you have any questions about your order, please call us at')
        }}
        <a :href="`tel:${meta.phoneNumber}`" class="underline">{{
          meta.phoneNumber
        }}</a
        >.
      </div>
      <div v-if="order.estimatedArrivalAt" class="mt-5 text-center">
        <span class="font-medium">{{ trans('Estimated arrival') }}:</span><br />
        {{ formatDateTime(order.estimatedArrivalAt, 'MMM d, hh:mm a') }}
      </div>
      <div class="mt-10">
        <AVerticalStep
          v-for="(historyItem, historyItemIndex) in historyItems"
          :key="historyItemIndex"
          :type="historyItem.type"
          :last="historyItemIndex === historyItems.length - 1"
        >
          <template #title> {{ historyItem.title }} </template>
          <template #description> {{ historyItem.description }} </template>
        </AVerticalStep>
      </div>
      <div class="mt-8">
        <div
          v-for="(displayField, displayFieldIndex) in order.displayFields"
          :key="displayFieldIndex"
        >
          <strong>{{ displayField.name }}: </strong>
          {{ displayField.value }}
        </div>

        <div class="my-3">
          <div class="border-t border-theme-text/20" />
        </div>

        <div>
          <div
            v-for="(orderItem, orderItemIndex) in order.orderItems"
            :key="orderItemIndex"
            class="mb-4 last:mb-0"
          >
            <p>
              <strong>{{ orderItem.quantity }}x</strong>
              {{ localize(orderItem.productName) }}
              <template v-if="localize(orderItem.productVariantName)">
                - {{ localize(orderItem.productVariantName) }}
              </template>
              <template v-if="orderItem.modifiers.length > 0">
                ({{
                  orderItem.modifiers
                    .map(({ modifierName }) => localize(modifierName))
                    .join(', ')
                }})
              </template>
            </p>

            <p v-if="orderItem.price > 0">
              {{
                formatPrice(orderItem.price * orderItem.quantity, {
                  currencyCode: order.currencyCode,
                })
              }}
            </p>
          </div>
        </div>

        <div class="my-3">
          <div class="border-t border-theme-text/20" />
        </div>

        <div v-if="order.totalPrice > 0" class="space-y-1">
          <div class="flex justify-between">
            <strong>{{ trans('Subtotal') }}</strong>
            <span>{{
              formatPrice(order.subtotalPrice, {
                currencyCode: order.currencyCode,
              })
            }}</span>
          </div>
          <div
            v-for="priceAdjustment in order.priceAdjustments"
            :key="priceAdjustment.id"
            class="flex justify-between"
          >
            <span
              >{{ localize(priceAdjustment.name) }} ({{
                priceAdjustment.rate
              }}%)</span
            >
            <span>{{
              formatPrice(priceAdjustment.amount, {
                currencyCode: order.currencyCode,
              })
            }}</span>
          </div>
          <div class="flex justify-between font-medium">
            <span>{{ trans('Total amount') }}</span>
            <span>{{
              formatPrice(order.totalPrice, {
                currencyCode: order.currencyCode,
              })
            }}</span>
          </div>
        </div>
      </div>

      <div class="mb-8 mt-12 flex justify-center">
        <RouterLink :to="{ name: 'root' }" class="menu-button-primary w-max">
          {{ trans('Back to menu') }}
        </RouterLink>
      </div>
    </div>
    <div v-else-if="!loaded" class="mt-10 text-center text-xl">
      {{ trans('Loading') }}...
    </div>
    <div v-else class="mt-10 text-center text-xl">
      {{ trans('Order not found') }}...
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
import MainLayout from '@menu/Shared/MainLayout.vue';
import ADeepCircleButton from '@menu/Shared/ADeepCircleButton.vue';
import axios from 'axios';
import AVerticalStep from '@menu/Shared/AVerticalStep.vue';
import ArrowLeftBold from '@/phosphoricons/ArrowLeftBold.vue';
import { trans } from '@menu/Utils/i18n';
import { localize } from '@menu/Utils/i18n';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { OrderResource } from '@menu/Types/Resources/OrderResource';
import { useRoute, useRouter } from 'vue-router';

import formatPrice from '@menu/Utils/formatPrice';
import formatDateTime from '@menu/Utils/formatDateTime';
import getRestaurant from '@menu/Utils/getRestaurant';

const route = useRoute();
const router = useRouter();
const restaurant = getRestaurant();

const loaded = ref(false);
const order = ref<OrderResource | null>(null);
const meta = ref<{
  phoneNumber: string | null;
} | null>(null);

type HistoryItem = {
  title: string;
  description: string;
  type: 'neutral' | 'success' | 'error';
  current?: boolean;
};

const historyItems = computed(() => {
  if (!order.value) {
    return [];
  }

  const items: HistoryItem[] = [];

  const addItem = ({ title, description, type, current }: HistoryItem) => {
    items.push({
      title,
      description,
      type,
    });

    if (current) {
      items.forEach((item) => {
        if (item.type === 'neutral') {
          item.type = 'success';
        }
      });
    }
  };

  addItem({
    title: trans('Order placed'),
    description: trans('Your order has been placed.'),
    current: order.value.status === 'NEW',
    type: 'neutral',
  });

  if (order.value.status === 'REJECTED') {
    addItem({
      title: trans('Rejected'),
      description: trans('Reason') + ': ' + order.value.rejectionReason,
      current: true,
      type: 'error',
    });
  } else {
    addItem({
      title: trans('Preparing'),
      description: trans('Your order is being prepared.'),
      current: order.value.status === 'ACCEPTED',
      type: 'neutral',
    });

    if (order.value.type === 'PICKUP') {
      addItem({
        title: trans('Ready for pickup'),
        description: trans('Your order is ready for pickup.'),
        current: order.value.status === 'READY_FOR_PICKUP',
        type: 'neutral',
      });
    }

    if (order.value.type === 'DELIVERY') {
      addItem({
        title: trans('Out for delivery'),
        description: trans('Your order is out for delivery.'),
        current: order.value.status === 'OUT_FOR_DELIVERY',
        type: 'neutral',
      });
    }

    addItem({
      title: trans('Completed'),
      description: trans('Your order has been completed.'),
      current: order.value.status === 'COMPLETED',
      type: 'neutral',
    });
  }

  return items;
});

const intervalId = ref<ReturnType<typeof setInterval>>();

onMounted(() => {
  loadOrder();

  intervalId.value = setInterval(() => {
    if (order.value?.status !== 'COMPLETED') {
      loadOrder();
    }
  }, 10000);
});

onUnmounted(() => {
  clearInterval(intervalId.value);
});

const loadOrder = () => {
  axios
    .get('/api/orders/' + route.params.orderUuid)
    .then((response) => {
      order.value = response.data.data;
      meta.value = response.data.meta;
      document.title = `Order #${order.value?.orderNumber} - ${restaurant.name}`;
    })
    .finally(() => {
      loaded.value = true;
    });
};
</script>
